import * as React from 'react';
import styled from 'styled-components';
import ActivityCard from './ActivityCard';
import strings from '../../../strings/strings.json';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { BodyLarge, BodySmall, Flex, FlexColMd, FlexColSm, FlexSmAlignCenter } from '../../../components/Elements';
import { Module, ModuleType } from '../../../models/Module';
import { breakpoints, FONTSIZE, sizes, theme } from '../../../theme';
import { Activity, ActivityType, GenerateType } from 'models/Activity';
import useModifyRequest from '../../../hooks/useModifyRequest';
import { APIError } from '../../../lib/_api-helpers';
import Notification, { NotificationType } from '../../../components/notifiers/Notification';
import { reorder } from '../../../lib/reorderList';
import EditIcon from '../../../assets/icons/controls/EditIcon';
import { AccordionPanelHeaderTitle } from 'components/accordion/AccordionPanelTitle';
import { AccordionPanelToggle } from 'components/accordion/AccordionPanelToggle';
import { animated, useSpring } from '@react-spring/web';
import AddIcon from '../../../assets/icons/controls/AddIcon';
import AccessibilityNotifier from '../../../components/notifiers/AccessibilityNotifier';
import Button from '../../../components/buttons/Button';
import { useHeight } from '../../../hooks/useHeight';
import WarningIcon from 'assets/icons/indicators/WarningIcon';
import { ErrorMessage } from 'components/forms/FormComponents';
import GenerateModuleQuestionnaireForm from './questionnaire/GenerateModuleQuestionnaireForm';
import Modal from 'components/modals/Modal';
import QuestionnaireIcon from 'assets/icons/pathway/QuestionnaireIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import useWindowWidth from 'hooks/useWindowWidth';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import EditModuleForm from './moduleForms/EditModuleForm';
import ActivityTypeSelectionForm from './ActivityTypeSelectionForm';
import { ContentComponent } from './stepComponents/CourseStructureAndContentComponent';

type ModuleAccordionProps = {
    module: Module;
    activeModuleId?: string;
    dragHandleProps?: any;
    activitiesEditable?: boolean;
    activitiesSortable?: boolean;
    moduleEditable?: boolean;
    editActivityClick?: (moduleId: string, activityId: string) => void;
    showIncompleteStepsWarning?: boolean;
    setComponent?: (value: React.SetStateAction<ContentComponent>) => void;
};

export default function ModuleAccordion({
    module,
    activeModuleId,
    dragHandleProps,
    activitiesEditable = false,
    activitiesSortable = false,
    moduleEditable = false,
    showIncompleteStepsWarning,
    setComponent,
}: ModuleAccordionProps): JSX.Element {
    const { editMode, refetchCourse } = React.useContext(BuildCourseContext);
    const { isMobileWidth } = useWindowWidth();

    const [errors, setErrors] = React.useState<APIError[]>([]);
    const [reorderSaved, setReorderSaved] = React.useState<boolean>(false);
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [localActivities, setLocalActivities] = React.useState<Activity[]>([]);
    const [showGenerateQuestionnaireModal, setShowGenerateQuestionnaireModal] = React.useState<boolean>(false);
    const [openEditModuleModal, setOpenEditModuleModal] = React.useState(false);
    const [openAddActivityModal, setOpenAddActivityModal] = React.useState(false);

    const editModuleButtonRef = React.useRef<HTMLButtonElement>(null);
    const addActivityButtonRef = React.useRef<HTMLButtonElement>(null);

    const [heightRef, height] = useHeight();

    const { height: animatedHeight } = useSpring({
        from: { height: 0 },
        to: {
            height: isExpanded ? height : 0,
        },
    });

    const { modifyData: reorderActivities, loading: reorderLoading } = useModifyRequest<string[], boolean>(
        `modules/${module.id}/reorder-activities`,
        'PUT',
    );

    const scrollToBottom = () => {
        window.scrollTo(0, document.body.scrollHeight);
    };

    React.useEffect(() => {
        if (activeModuleId && activeModuleId === module.id) {
            setIsExpanded(true);
            addActivityButtonRef.current?.focus();
            scrollToBottom();
        } else {
            setIsExpanded(false);
        }
    }, [activeModuleId]); // eslint-disable-line

    React.useEffect(() => {
        setLocalActivities(
            module.activities.sort((a, b) => {
                return a.order - b.order;
            }),
        );
    }, [module]);

    const showVariationTip = React.useMemo(() => {
        if (editMode || !moduleEditable) return null;
        if (!module.activities) return null;
        if (module.moduleType === ModuleType.LEARNER_LED && module.activities.length === 6) return null;
        if (module.moduleType === ModuleType.PRESENTATION && module.activities.length === 40) return null;

        const tasks = module.activities.filter((x) => x.type === ActivityType.TASK);
        const enoughContentToGenerateQuestionnaire =
            tasks.filter((t) => t.content && t.content.length > 0).length >= 2;

        const existingModuleQuestionnaire = module.activities.some(
            (a) =>
                a.generateContentStatus && a.generateContentStatus.generateType === GenerateType.MODULE_QUESTIONNAIRE,
        );

        if (enoughContentToGenerateQuestionnaire && !existingModuleQuestionnaire) return 'generateModuleQuestionnaire';

        const numberOfLiveTrainings = module.activities.filter((x) => x.type === ActivityType.LIVE_TRAINING).length;
        if (numberOfLiveTrainings > 3) return 'tooManyLiveTrainingActivities';

        const numberOfQuestionnaires = module.activities.filter((x) => x.type === ActivityType.QUESTIONNAIRE).length;
        if (numberOfQuestionnaires > 3) return 'tooManyQuestionnaires';
    }, [module.activities, editMode, moduleEditable, module.moduleType]);

    async function onDragEnd(result: DropResult) {
        if (!result.destination) return;

        setErrors([]);
        setReorderSaved(false);

        const oldActivities = [...localActivities];
        const newActivities = reorder(localActivities, result.source.index, result.destination.index);

        setLocalActivities(newActivities);

        const reorderResponse = await reorderActivities(newActivities.map((x) => x.id));

        const { errors } = reorderResponse;
        if (errors) {
            setLocalActivities(oldActivities);
            setErrors(errors);
            return;
        }

        if (!editMode) await refetchCourse();
        setReorderSaved(true);
    }

    const onDragStart = () => {
        if (reorderSaved) {
            setReorderSaved(false);
        }
    };

    const showAddButton =
        (module.moduleType === ModuleType.LEARNER_LED && module.activities.length < 12) ||
        (module.moduleType === ModuleType.PRESENTATION && module.activities.length < 400);

    const moduleTypeText =
        module.moduleType === ModuleType.LEARNER_LED
            ? strings.courseContentPage.moduleType.learnerled
            : strings.courseContentPage.moduleType.presentation;

    const closeEditModuleModal = () => {
        setOpenEditModuleModal(false);
        editModuleButtonRef.current?.focus();
    };

    const closeEditModuleModalAfterDeletion = () => {
        setOpenEditModuleModal(false);
    };

    const closeAddActivityModal = () => {
        setOpenAddActivityModal(false);
        addActivityButtonRef.current?.focus();
    };

    return (
        <>
            {openEditModuleModal && (
                <Modal
                    headlineText={strings.moduleForm.editModuleTitle}
                    onClickCloseButton={closeEditModuleModal}
                    modalBody={
                        <EditModuleForm
                            id={module.id}
                            title={module.title}
                            description={module.description}
                            closeFormAfterDeletion={closeEditModuleModalAfterDeletion}
                        />
                    }
                />
            )}
            {openAddActivityModal && module.moduleType === ModuleType.LEARNER_LED && (
                <Modal
                    headlineText={strings.activityTypeSelectionPage.instructionText}
                    onClickCloseButton={closeAddActivityModal}
                    modalBody={<ActivityTypeSelectionForm moduleId={module.id} setComponent={setComponent!} />}
                />
            )}

            <AccordionPanelContainer id={module.id}>
                <ModuleInfoGrid {...dragHandleProps}>
                    <AccordionPanelHeaderTitle title={module.title} />
                    <ModuleTypeAndDescriptionArea>
                        <BodySmall>
                            <strong>{strings.courseContentPage.moduleTypeText} </strong>
                            {moduleTypeText}
                        </BodySmall>
                        {module.description && (
                            <BodySmall>
                                <strong>{strings.courseContentPage.moduleDescriptionText} </strong>
                                {module.description}
                            </BodySmall>
                        )}
                    </ModuleTypeAndDescriptionArea>
                    {moduleEditable && (
                        <Edit>
                            <Button
                                ref={editModuleButtonRef}
                                title={strings.courseContentPage.editModuleButton}
                                aria={strings.courseContentPage.editModuleButtonAria}
                                onClick={() => setOpenEditModuleModal(true)}
                                icon={<EditIcon />}
                                fullwidth
                            />
                        </Edit>
                    )}
                    <AccordionPanelToggle onClick={() => setIsExpanded(!isExpanded)} isOpen={isExpanded} />
                </ModuleInfoGrid>
                {showIncompleteStepsWarning && module.activities.length === 0 && (
                    <Warning>
                        <WarningIcon />
                        <ErrorMessage>{strings.courseContentPage.addActivityToThisModule}</ErrorMessage>
                    </Warning>
                )}
                <animated.div style={{ overflow: 'hidden', height: animatedHeight }}>
                    <div ref={heightRef}>
                        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                            <Droppable
                                droppableId={`activities-list-${module.id}`}
                                type="activity-droppable"
                                isDropDisabled={!activitiesSortable}
                            >
                                {(provided, snapshot) => (
                                    <DropArea
                                        $isDraggingOver={snapshot.isDraggingOver}
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                    >
                                        <ActivitiesTitle $bold $isDraggingOver={snapshot.isDraggingOver}>
                                            {module.moduleType === ModuleType.LEARNER_LED
                                                ? strings.publishedCoursePage.activitiesHeader
                                                : strings.courseContentPage.slides}
                                        </ActivitiesTitle>
                                        {localActivities.map((activity, index) => {
                                            return (
                                                <Draggable
                                                    key={activity.id}
                                                    draggableId={activity.id}
                                                    index={index}
                                                    isDragDisabled={
                                                        reorderLoading || !activitiesSortable || !isExpanded // disable drag if collapsed
                                                    }
                                                >
                                                    {(provided, snapshot) => (
                                                        <DroppableItem
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            tabIndex={isExpanded ? 0 : -1} // make wrapper unfocusable when collapsed
                                                            aria-hidden={!isExpanded} // hide wrapper from screen readers
                                                        >
                                                            <ActivityCard
                                                                isDragging={snapshot.isDragging}
                                                                key={activity.id}
                                                                activity={activity}
                                                                editActivityClick={() =>
                                                                    setComponent!({
                                                                        name: 'editActivity',
                                                                        activityId: activity.id,
                                                                    })
                                                                }
                                                                activitiesEditable={
                                                                    editMode &&
                                                                    activity.type === ActivityType.QUESTIONNAIRE //questionnaires can't currently be edited
                                                                        ? false
                                                                        : activitiesEditable
                                                                }
                                                                unfocus={!isExpanded}
                                                            />
                                                        </DroppableItem>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                        {reorderLoading ? (
                                            <Notification
                                                notificationType={NotificationType.WARNING}
                                                title={strings.courseContentPage.reorderModulesSaving}
                                            />
                                        ) : reorderSaved ? (
                                            <Notification
                                                notificationType={NotificationType.SUCCESS}
                                                title={strings.courseContentPage.reorderModulesSaved}
                                            />
                                        ) : null}

                                        {errors && errors.length > 0 && !reorderLoading && (
                                            <Notification
                                                notificationType={NotificationType.DANGER}
                                                title={strings.courseContentPage.reorderActivitiesError}
                                                description={strings.courseContentPage.reorderActivitiesErrorDetail}
                                            />
                                        )}

                                        {showVariationTip === 'generateModuleQuestionnaire' ? (
                                            <AccessibilityNotifier
                                                accessibilityGuideKey={showVariationTip}
                                                fixButtonName={
                                                    isMobileWidth
                                                        ? strings.accessibilityGuide.generateQuestionnaireButton
                                                              .titleMobile
                                                        : strings.accessibilityGuide.generateQuestionnaireButton.title
                                                }
                                                fixButtonAria={
                                                    strings.accessibilityGuide.generateQuestionnaireButton.aria
                                                }
                                                fixButtonIcon={
                                                    <QuestionnaireIcon
                                                        colour={theme.secondaryButtonTextColour}
                                                        size={IconSize.SMALL}
                                                    />
                                                }
                                                fixButtonFunction={() => {
                                                    setShowGenerateQuestionnaireModal(true);
                                                }}
                                            />
                                        ) : (
                                            <AccessibilityNotifier accessibilityGuideKey={showVariationTip!} />
                                        )}

                                        {showGenerateQuestionnaireModal && (
                                            <Modal
                                                onClickCloseButton={() => setShowGenerateQuestionnaireModal(false)}
                                                modalBody={
                                                    <GenerateModuleQuestionnaireForm
                                                        moduleId={module.id}
                                                        moduleTitle={module.title}
                                                        tasks={module.activities}
                                                        returnToModulesList={() =>
                                                            setShowGenerateQuestionnaireModal(false)
                                                        }
                                                        hideHeader
                                                    />
                                                }
                                                headlineText={strings.questionnaireGenerateForm.instruction}
                                            />
                                        )}

                                        {showAddButton && activitiesEditable && (
                                            <AddActivityButtonContainer>
                                                <Button
                                                    ref={addActivityButtonRef}
                                                    title={
                                                        module.moduleType === ModuleType.LEARNER_LED
                                                            ? strings.courseContentPage.addActivityButton
                                                            : strings.courseContentPage.addSlideButton
                                                    }
                                                    aria={
                                                        module.moduleType === ModuleType.LEARNER_LED
                                                            ? strings.courseContentPage.addActivityButtonAria
                                                            : strings.courseContentPage.addSlideButtonAria
                                                    }
                                                    onClick={
                                                        module.moduleType === ModuleType.LEARNER_LED
                                                            ? () => setOpenAddActivityModal(true)
                                                            : () =>
                                                                  setComponent!({
                                                                      name: 'addSlide',
                                                                      moduleId: module.id,
                                                                  })
                                                    }
                                                    icon={<AddIcon />}
                                                    fullwidth
                                                    unfocus={!isExpanded}
                                                />
                                            </AddActivityButtonContainer>
                                        )}
                                    </DropArea>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>
                </animated.div>
            </AccordionPanelContainer>
        </>
    );
}

const ActivitiesTitle = styled(BodyLarge)<{ $isDraggingOver: boolean }>`
    color: ${({ $isDraggingOver }) => {
        return $isDraggingOver ? theme.primaryButtonTextColour : theme.textColour;
    }};
`;

const DropArea = styled(FlexColSm)<{ $isDraggingOver: boolean }>`
    background-color: ${({ $isDraggingOver }) => {
        return $isDraggingOver ? theme.primaryButtonHoverBackgroundColour : theme.cardSecondaryBackgroundColour;
    }};
    border-radius: ${sizes.spacingSm};
    padding: ${sizes.spacingMd};
`;

const DroppableItem = styled.div`
    margin-bottom: ${sizes.spacingSm};
    background-color: ${theme.cardBackground};
    border-radius: ${sizes.borderRadiusLg};
    &:focus {
        border: 2px solid ${theme.linkFocusBorderColour};
        outline: ${theme.linkFocusBorderColour};
        background-color: ${theme.buttonFocusBackgroundColour};
        border-radius: 12px;
    }
`;

const Edit = styled(Flex)`
    grid-area: edit;
    align-items: flex-start;
    font-size: ${FONTSIZE.BodyRegular};
`;

const ModuleInfoGrid = styled.div`
    display: grid;
    grid-template-areas:
        'title edit toggle'
        'moduleType moduleType moduleType';
    grid-template-columns: 1fr auto auto;
    gap: ${sizes.spacingMd};

    @media (max-width: ${breakpoints.md}) {
        grid-template-areas:
            'title  toggle'
            'moduleType moduleType'
            'edit edit';
    }
`;

const ModuleTypeAndDescriptionArea = styled(FlexColSm)`
    grid-area: moduleType;
`;

interface AccordionPanelContainerProps {
    complete?: boolean;
}

const AccordionPanelContainer = styled(FlexColMd)<AccordionPanelContainerProps>`
    width: 100%;
    padding: ${sizes.spacingMd};
    box-shadow: 2px 4px 6px rgba(0, 0, 0, 0.1);

    background-color: ${theme.cardBackground};
    border-radius: ${sizes.borderRadiusLg};
    border: 1px solid ${({ complete }) => (complete ? theme.notificationSuccessBorder : theme.cardBorder)};
`;

const AddActivityButtonContainer = styled.div`
    width: 100%;
    padding-bottom: ${sizes.spacingMd};
`;

const Warning = styled(FlexSmAlignCenter)`
    margin-top: ${sizes.spacingMd};
`;
